/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';

/**
 * RGBARRY-CUSTOMIZATION (W-23789028, slice 4): pushes a `page_error` event to the GTM dataLayer so
 * the customer's container "GA4 - Error" tag fires on error / 404 pages (SFRA parity).
 *
 * WHY A DIRECT dataLayer PUSH (not `useAnalytics` / the adapter pipeline). This renders from the
 * root `ErrorBoundary`, which React Router mounts in place of `<App/>` — i.e. OUTSIDE the analytics
 * providers (ConfigProvider / SiteProvider / AuthProvider are composed inside `App`). `useAnalytics`
 * (and therefore the consent-gated `ga4-datalayer` adapter) is unavailable here, so this writes
 * straight to `window.dataLayer`, the same sink that adapter writes to. Consent is still enforced
 * downstream by GTM's Google Consent Mode — the mechanism that gates every GA4 tag — so no hit
 * reaches GA4 without consent.
 *
 * Client-only (no SSR push) and fires once per mount (`sent` guard) so a single error render does
 * not emit duplicate `page_error` events across React re-renders.
 */
export function PageErrorTracker({ status, details }: { status?: number; details?: string }) {
    const sent = useRef(false);

    useEffect(() => {
        if (sent.current || typeof window === 'undefined') return;
        sent.current = true;

        // `pageError` is a concise label: HTTP status + status text/message when present. Route
        // errors provide a safe statusText (e.g. "Not Found"); thrown Errors provide `message`,
        // which is capped to keep the label bounded.
        const label = [status, details?.slice(0, 200)].filter(Boolean).join(' ').trim();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'page_error', pageError: label || 'Unknown error' });
    }, [status, details]);

    return null;
}
